import React, { useMemo, useState } from "react";
import { ChevronDown, Filter, FilterX, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useI18n } from "../context/LanguageContext";

export const BLANK = "__blank__";

const sortValues = (list) => {
    const allNum = list.every(({ v }) => v === BLANK || !Number.isNaN(Number(v)));
    return [...list].sort((a, b) => {
        if (a.v === BLANK) return 1;
        if (b.v === BLANK) return -1;
        return allNum ? Number(a.v) - Number(b.v) : a.v.localeCompare(b.v, undefined, { numeric: true, sensitivity: "base" });
    });
};

// Excel-like AutoFilter: lists every unique value; changes are applied with OK (Cancel discards).
export function ColumnFilter({ colKey, label, getValues, selected, onChange }) {
    const { t } = useI18n();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [values, setValues] = useState([]);
    const [draft, setDraft] = useState(new Set());
    const sorted = useMemo(() => sortValues(values), [values]);
    const shown = q.trim() ? sorted.filter(({ v }) => v !== BLANK && v.toLowerCase().includes(q.trim().toLowerCase())) : sorted;
    const active = !!selected;
    const openChange = (o) => {
        setOpen(o);
        setQ("");
        if (o) {
            const vals = getValues();
            setValues(vals);
            setDraft(new Set(selected ? vals.map((x) => x.v).filter((v) => selected.has(v)) : vals.map((x) => x.v)));
        }
    };
    const toggle = (v) => setDraft((d) => { const n = new Set(d); if (n.has(v)) n.delete(v); else n.add(v); return n; });
    const setShown = (on) => setDraft((d) => { const n = new Set(d); shown.forEach(({ v }) => (on ? n.add(v) : n.delete(v))); return n; });
    const allShownOn = shown.length > 0 && shown.every(({ v }) => draft.has(v));
    const someShownOn = shown.some(({ v }) => draft.has(v));
    const total = values.reduce((s, x) => s + x.n, 0);
    const matched = values.reduce((s, x) => s + (draft.has(x.v) ? x.n : 0), 0);
    const apply = () => {
        // With a search term Excel keeps only the matching checked values.
        const set = q.trim() ? new Set(shown.map(({ v }) => v).filter((v) => draft.has(v))) : draft;
        onChange(values.every(({ v }) => set.has(v)) ? undefined : set);
        setOpen(false);
    };
    return (
        <Popover open={open} onOpenChange={openChange}>
            <PopoverTrigger asChild>
                <button data-testid={`col-filter-btn-${colKey}`} data-active={active ? "1" : "0"} title={t("cf_title")}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`shrink-0 w-[18px] h-[18px] flex items-center justify-center rounded-sm border transition-colors ${active ? "text-white bg-blue-600 border-blue-400" : "text-slate-300 bg-[#182232] border-[#334155] hover:bg-[#223047] hover:text-white"}`}>
                    {active ? <Filter className="w-3 h-3 fill-white" /> : <ChevronDown className="w-3 h-3" />}
                </button>
            </PopoverTrigger>
            <PopoverContent data-testid={`col-filter-pop-${colKey}`} align="start" onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
                className="w-72 p-0 bg-[#111827] border-[#1e293b] text-slate-200">
                <div className="px-3 pt-3 pb-2 border-b border-[#1e293b]">
                    <div className="text-xs font-mono uppercase tracking-wider text-slate-400 truncate">{label}</div>
                    <button data-testid={`col-filter-clear-${colKey}`} disabled={!active} onClick={() => { onChange(undefined); setOpen(false); }}
                        className="mt-2 w-full flex items-center gap-1.5 text-xs px-2 h-7 rounded text-rose-300 hover:bg-rose-500/10 disabled:text-slate-600 disabled:hover:bg-transparent transition-colors">
                        <FilterX className="w-3.5 h-3.5" /> {t("cf_clear_from", { c: label })}
                    </button>
                    <div className="relative mt-1">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input data-testid={`col-filter-search-${colKey}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("cf_search")} autoFocus
                            className="w-full h-8 pl-7 pr-2 bg-[#0b0f17] border border-[#1e293b] rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500/60" />
                    </div>
                </div>
                <div className="max-h-64 overflow-auto py-1 tray-scroll border-b border-[#1e293b]">
                    {shown.length > 0 && (
                        <label className="flex items-center gap-2 px-3 py-1 text-xs font-semibold cursor-pointer hover:bg-[#182232]">
                            <input data-testid={`col-filter-toggle-all-${colKey}`} type="checkbox" checked={allShownOn}
                                ref={(el) => { if (el) el.indeterminate = !allShownOn && someShownOn; }}
                                onChange={() => setShown(!allShownOn)} className="accent-blue-500" />
                            <span className="flex-1">({t(q.trim() ? "cf_select_all_results" : "cf_select_all")})</span>
                        </label>
                    )}
                    {shown.map(({ v, n }) => (
                        <label key={v} data-testid={`col-filter-opt-${colKey}-${v}`} className="flex items-center gap-2 px-3 py-1 text-xs cursor-pointer hover:bg-[#182232]">
                            <input type="checkbox" checked={draft.has(v)} onChange={() => toggle(v)} className="accent-blue-500" />
                            <span className="flex-1 truncate">{v === BLANK ? `(${t("cf_blanks")})` : v}</span>
                            <span className="text-[11px] font-mono text-slate-400">{n}</span>
                        </label>
                    ))}
                    {shown.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">{t("cf_no_values")}</div>}
                </div>
                <div className="px-3 py-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <span data-testid={`col-filter-count-${colKey}`} className="text-[11px] font-mono text-slate-400 whitespace-nowrap">{t("cf_found", { n: matched, total })}</span>
                        <div className="flex gap-1">
                            <button data-testid={`col-filter-all-${colKey}`} onClick={() => setShown(true)} className="text-[11px] px-1.5 h-6 rounded hover:bg-[#182232] text-slate-300 transition-colors whitespace-nowrap">{t("cf_select_all")}</button>
                            <button data-testid={`col-filter-none-${colKey}`} onClick={() => setShown(false)} className="text-[11px] px-1.5 h-6 rounded hover:bg-[#182232] text-slate-300 transition-colors whitespace-nowrap">{t("cf_clear_all")}</button>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button data-testid={`col-filter-ok-${colKey}`} disabled={draft.size === 0 || (q.trim() && !someShownOn)} onClick={apply}
                            className="h-8 px-4 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-40 transition-colors">OK</button>
                        <button data-testid={`col-filter-cancel-${colKey}`} onClick={() => setOpen(false)}
                            className="h-8 px-3 rounded border border-[#1e293b] hover:bg-[#182232] text-xs transition-colors">{t("bd_cancel")}</button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
